import { Room, RoomEvent } from 'livekit-client';

class LiveKitService {
  constructor() {
    this.room = null;
    this.isConnected = false;
    this.onAudioData = null;
    this.onConnectionStateChange = null;
    this.onParticipantConnected = null;
    this.onParticipantDisconnected = null;
    this.onTrackSubscribed = null;
    this.onTrackUnsubscribed = null;
    this.onDataReceived = null;
    this.onActiveSpeakersChanged = null;
  }

  async connect(livekitUrl, token, roomName) {
    try {
      console.log('🔗 LiveKit: Starting connection...', { livekitUrl, roomName });
      
      this.room = new Room({
        adaptiveStream: true,
        dynacast: true,
      });

      // Set up event listeners with detailed logging
      this.setupEventListeners();

      console.log('🔗 LiveKit: Connecting to room...');
      await this.room.connect(livekitUrl, token, {
        autoSubscribe: true,
      });

      console.log('✅ LiveKit: Successfully connected to room');
      console.log('🔊 LiveKit: Local participant:', this.room.localParticipant.identity);
      console.log('👥 LiveKit: Remote participants:', this.room.participants ? this.room.participants.size : 0);

      this.isConnected = true;
      return true;
    } catch (error) {
      console.error('❌ LiveKit: Connection failed:', error);
      throw error;
    }
  }

  setupEventListeners() {
    if (!this.room) return;

    // Connection state changes
    this.room.on(RoomEvent.ConnectionStateChanged, (state) => {
      console.log('🔄 LiveKit: Connection state changed:', state);
      if (this.onConnectionStateChange) {
        this.onConnectionStateChange(state);
      }
    });

    // Participant connected
    this.room.on(RoomEvent.ParticipantConnected, (participant) => {
      console.log('👤 LiveKit: Participant connected:', participant.identity);
      if (this.onParticipantConnected) {
        this.onParticipantConnected(participant);
      }
    });

    this.room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      // 'speakers' is an array of Participant objects who are currently speaking
      if (speakers.length > 0) {
        console.log('Active speakers:', speakers.map(p => p.identity));
      } else {
        console.log('No one is currently speaking.');
      }
      
      // Notify callback
      if (this.onActiveSpeakersChanged) {
        this.onActiveSpeakersChanged(speakers);
      }
    });

    // Participant disconnected
    this.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      console.log('👤 LiveKit: Participant disconnected:', participant.identity);
      if (this.onParticipantDisconnected) {
        this.onParticipantDisconnected(participant);
      }
    });

    // Track subscribed
    this.room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      console.log('🎵 LiveKit: Track subscribed:', {
        track: track.kind,
        participant: participant.identity,
        sid: publication.trackSid
      });
      if (this.onTrackSubscribed) {
        this.onTrackSubscribed(track, publication, participant);
      }
    });

    // Track unsubscribed
    this.room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
      console.log('🎵 LiveKit: Track unsubscribed:', {
        track: track.kind,
        participant: participant.identity,
        sid: publication.trackSid
      });
      if (this.onTrackUnsubscribed) {
        this.onTrackUnsubscribed(track, publication, participant);
      }
    });

    // Data received
    this.room.on(RoomEvent.DataReceived, (payload, participant) => {
      console.log('📨 LiveKit: Data received:', {
        from: participant.identity,
        data: payload
      });
      if (this.onDataReceived) {
        this.onDataReceived(payload, participant);
      }
    });


    // Audio levels
    this.room.on(RoomEvent.AudioPlaybackStatusChanged, (playing) => {
      console.log('🔊 LiveKit: Audio playback status:', playing);
    });

    // Local participant events
    this.room.localParticipant.on('trackPublished', (publication) => {
      console.log('🎤 LiveKit: Local track published:', publication.track.kind);
    });

    this.room.localParticipant.on('trackUnpublished', (publication) => {
      console.log('🎤 LiveKit: Local track unpublished:', publication.track.kind);
    });
  }

  async enableMicrophone() {
    try {
      if (!this.room) {
        console.error('❌ LiveKit: Cannot enable microphone - not connected');
        return false;
      }

      console.log('🎤 LiveKit: Enabling microphone...');
      await this.room.localParticipant.setMicrophoneEnabled(true);
      console.log('✅ LiveKit: Microphone enabled');
      return true;
    } catch (error) {
      console.error('❌ LiveKit: Failed to enable microphone:', error);
      return false;
    }
  }

  async disableMicrophone() {
    try {
      if (!this.room) {
        console.error('❌ LiveKit: Cannot disable microphone - not connected');
        return false;
      }

      console.log('🎤 LiveKit: Disabling microphone...');
      await this.room.localParticipant.setMicrophoneEnabled(false);
      console.log('✅ LiveKit: Microphone disabled');
      return true;
    } catch (error) {
      console.error('❌ LiveKit: Failed to disable microphone:', error);
      return false;
    }
  }

  async disableCamera() {
    try {
      if (!this.room) {
        console.error('❌ LiveKit: Cannot disable camera - not connected');
        return false;
      }
  
      console.log('🎤 LiveKit: Disabling camera...');
      await this.room.localParticipant.setCameraEnabled(false);
      console.log('✅ LiveKit: Camera disabled');
      return true;
    } catch (error) {
      console.error('❌ LiveKit: Failed to disable camera:', error);
      return false;
    }
  }

  async enableSpeaker() {
    try {
      if (!this.room) {
        console.error('❌ LiveKit: Cannot enable speaker - not connected');
        return false;
      }

      console.log('🔊 LiveKit: Enabling speaker...');
      await this.room.localParticipant.setCameraEnabled(false);
      console.log('✅ LiveKit: Speaker enabled');
      return true;
    } catch (error) {
      console.error('❌ LiveKit: Failed to enable speaker:', error);
      return false;
    }
  }

  getConnectionState() {
    if (!this.room) return 'disconnected';
    return this.room.connectionState;
  }

  getParticipants() {
    if (!this.room) return [];
    
    // Get all participants including local and remote
    const participants = [];
    
    // Add local participant
    if (this.room.localParticipant) {
      participants.push(this.room.localParticipant);
    }
    
    // Add remote participants
    if (this.room.participants) {
      participants.push(...Array.from(this.room.participants.values()));
    }
    
    return participants;
  }

  getLocalParticipant() {
    if (!this.room) return null;
    return this.room.localParticipant;
  }

  async stopAllMediaTracks() {
    console.log('🛑 LiveKit: Starting comprehensive media track cleanup...');
    
    if (!this.room) {
      console.log('🔍 LiveKit: No room found, skipping track cleanup');
      return;
    }
    
    try {
      // Stop local participant tracks
      if (this.room.localParticipant) {
        console.log('🎤 LiveKit: Stopping local participant tracks...');
        
        // Method 1: Stop tracks by source type
        const micTrack = this.room.localParticipant.getTrack('microphone');
        if (micTrack && micTrack.mediaStreamTrack) {
          console.log('🛑 Stopping microphone track via source');
          micTrack.mediaStreamTrack.stop();
        }
        
        const cameraTrack = this.room.localParticipant.getTrack('camera');
        if (cameraTrack && cameraTrack.mediaStreamTrack) {
          console.log('🛑 Stopping camera track via source');
          cameraTrack.mediaStreamTrack.stop();
        }
        
        // Method 2: Stop all published audio tracks
        if (this.room.localParticipant.audioTracks) {
          this.room.localParticipant.audioTracks.forEach((track) => {
            console.log('🛑 Stopping audio track:', track.sid);
            if (track.mediaStreamTrack) {
              track.mediaStreamTrack.stop();
            }
          });
        }
        
        // Method 3: Stop all published video tracks  
        if (this.room.localParticipant.videoTracks) {
          this.room.localParticipant.videoTracks.forEach((track) => {
            console.log('🛑 Stopping video track:', track.sid);
            if (track.mediaStreamTrack) {
              track.mediaStreamTrack.stop();
            }
          });
        }
        
        // Method 4: Disable microphone and camera before unpublishing
        try {
          console.log('📴 LiveKit: Disabling microphone and camera...');
          await this.room.localParticipant.setMicrophoneEnabled(false);
          await this.room.localParticipant.setCameraEnabled(false);
        } catch (disableError) {
          console.warn('⚠️ Error disabling devices:', disableError);
        }
        
        // Method 5: Unpublish all tracks
        try {
          console.log('🚫 LiveKit: Unpublishing all tracks...');
          await this.room.localParticipant.unpublishTracks();
        } catch (unpublishError) {
          console.warn('⚠️ Error unpublishing tracks:', unpublishError);
        }
        
        // Method 6: Additional cleanup for any remaining tracks
        const allTracks = this.room.localParticipant.tracks;
        if (allTracks) {
          allTracks.forEach((trackPub) => {
            if (trackPub.track && trackPub.track.mediaStreamTrack) {
              console.log('🛑 Final cleanup - stopping track:', trackPub.track.kind);
              trackPub.track.mediaStreamTrack.stop();
            }
          });
        }
      }
      
      console.log('✅ LiveKit: Comprehensive media track cleanup completed');
    } catch (error) {
      console.error('❌ LiveKit: Error during media track cleanup:', error);
      
      // Emergency fallback: Try to stop any remaining tracks
      try {
        if (this.room?.localParticipant?.tracks) {
          this.room.localParticipant.tracks.forEach((trackPub) => {
            if (trackPub.track?.mediaStreamTrack) {
              console.log('🚨 Emergency stopping track:', trackPub.track.kind);
              trackPub.track.mediaStreamTrack.stop();
            }
          });
        }
      } catch (emergencyError) {
        console.error('🚨 Emergency cleanup also failed:', emergencyError);
      }
    }
  }

  async disconnect() {
    try {
      if (!this.room) {
        console.log('🔗 LiveKit: Already disconnected');
        return;
      }

      console.log('🔗 LiveKit: Starting disconnect process...');
      
      // Stop all media tracks BEFORE disconnecting
      await this.stopAllMediaTracks();
      
      console.log('🔗 LiveKit: Disconnecting from room...');
      await this.room.disconnect();
      this.room = null;
      this.isConnected = false;
      console.log('✅ LiveKit: Disconnected successfully');
    } catch (error) {
      console.error('❌ LiveKit: Error during disconnect:', error);
      // Even if there's an error, clear the room reference
      this.room = null;
      this.isConnected = false;
    }
  }

  // Set callback functions
  setOnAudioData(callback) {
    this.onAudioData = callback;
  }

  setOnConnectionStateChange(callback) {
    this.onConnectionStateChange = callback;
  }

  setOnParticipantConnected(callback) {
    this.onParticipantConnected = callback;
  }

  setOnParticipantDisconnected(callback) {
    this.onParticipantDisconnected = callback;
  }

  setOnTrackSubscribed(callback) {
    this.onTrackSubscribed = callback;
  }

  setOnTrackUnsubscribed(callback) {
    this.onTrackUnsubscribed = callback;
  }

  setOnDataReceived(callback) {
    this.onDataReceived = callback;
  }

  setOnActiveSpeakersChanged(callback) {
    this.onActiveSpeakersChanged = callback;
  }

  // Microphone mute/unmute
  async setMicrophoneMuted(muted) {
    if (!this.room || !this.room.localParticipant) {
      throw new Error('Room or local participant not available');
    }
    
    await this.room.localParticipant.setMicrophoneEnabled(!muted);
    return true;
  }

  // Get current microphone mute state
  isMicrophoneMuted() {
    if (!this.room || !this.room.localParticipant) return true; // default to muted when unknown
    try {
      // Check track publication state for microphone
      const micPub = this.room.localParticipant.getTrack?.('microphone');
      if (micPub && typeof micPub.isMuted === 'boolean') {
        return micPub.isMuted;
      }
      // Fallback to participant flag if available
      if (typeof this.room.localParticipant.isMicrophoneEnabled === 'boolean') {
        return !this.room.localParticipant.isMicrophoneEnabled;
      }
    } catch (_) {}
    // Conservative default: treat as muted to avoid unexpected hot mic
    return true;
  }

  // Send data message via LiveKit data channel
  async sendData(data, reliable = true) {
    try {
      if (!this.room || !this.room.localParticipant) {
        console.error('❌ LiveKit: Cannot send data - not connected');
        return false;
      }

      const message = typeof data === 'string' ? data : JSON.stringify(data);
      const messageBytes = new TextEncoder().encode(message);
      
      console.log('📤 LiveKit: Sending data message:', data);
      await this.room.localParticipant.publishData(messageBytes, reliable);
      console.log('✅ LiveKit: Data message sent successfully');
      return true;
    } catch (error) {
      console.error('❌ LiveKit: Failed to send data message:', error);
      return false;
    }
  }

  // Send data to specific participants (if needed)
  async sendDataToParticipants(data, participantIds = [], reliable = true) {
    try {
      if (!this.room || !this.room.localParticipant) {
        console.error('❌ LiveKit: Cannot send data - not connected');
        return false;
      }

      const message = typeof data === 'string' ? data : JSON.stringify(data);
      const messageBytes = new TextEncoder().encode(message);
      
      console.log('📤 LiveKit: Sending data message to participants:', { data, participantIds });
      await this.room.localParticipant.publishData(messageBytes, reliable, participantIds);
      console.log('✅ LiveKit: Data message sent to participants successfully');
      return true;
    } catch (error) {
      console.error('❌ LiveKit: Failed to send data message to participants:', error);
      return false;
    }
  }
}

const livekitService = new LiveKitService();
export default livekitService;